import type { NetPriceData } from "~/models/PriceData";
import type { AddToCartSource, OrderType } from "~/feature/Analytics/types";
import type { ProductData } from "~/models/ProductData";
import type { LineItem } from "~/feature/cart/cart/models/CartData";
import type { SalesPartner } from "~/feature/Analytics/ga/salesPartner";
import type { SearchPageType } from "~/Search/useSearchPageType";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import type { PurchasePlace, PurchaseType } from "~/feature/Analytics/ga/recommendations/types";

type SingleOrArray<T> = T | T[];

declare interface AddToCartMessage {
  products: SingleOrArray<{
    product: ProductData;
    quantity: number;
    price?: NetPriceData;
    packaging: {
      code: string;
      size: number;
    };
  }>;
  context: {
    source: AddToCartSource;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  };
}

declare interface PlaceOrderMessage {
  orderId: string;
  type: OrderType;
  entries: {
    lineItem: LineItem;
    product: ProductDescriptor;
  }[];
  paymentType: string;
  salesPartner?: SalesPartner; // In case of B2C purchases
}
declare interface SearchSuccessMessage {
  query: string;
  resultAmount: number;
  pageSize: number;
  pageNumber: number; // 0 based
  webpageType: SearchPageType;
}

// Define a type map for message types and their corresponding details
declare interface MQMessageTypeMap {
  addToCart: AddToCartMessage;
  placeOrder: PlaceOrderMessage;
  searchSuccess: SearchSuccessMessage;
  searchPageOpen: object;
}

type MessageHandler<T> = (message: T) => void;

export class AnalyticsMQ {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private subscribers: Map<keyof MQMessageTypeMap, Set<MessageHandler<any>>> = new Map();

  static instance: AnalyticsMQ = new AnalyticsMQ();

  private constructor() {}

  subscribe<T extends keyof MQMessageTypeMap>(type: T, handler: MessageHandler<MQMessageTypeMap[T]>): () => void {
    if (!this.subscribers.has(type)) {
      this.subscribers.set(type, new Set());
    }

    this.subscribers.get(type)!.add(handler);

    return () => {
      this.subscribers.get(type)!.delete(handler);
    };
  }

  push<T extends keyof MQMessageTypeMap>(type: T, message: MQMessageTypeMap[T]): void {
    const handlers = this.subscribers.get(type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(message);
        } catch (e) {
          console.error(e);
        }
      });
    }
  }
}
